'use client'

/* eslint-disable @next/next/no-img-element */

import React from 'react'

import type { Header as HeaderType, HeaderNavItem, HeaderNavItemDropdown } from '@/Header/types'

import Link from 'next/link'
import { ArrowRight, SearchIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import { usePathname } from 'next/navigation'
import { Dropdown } from './Dropdown'
import { CMSLink } from '@/components/Link'

interface HeaderNavProps {
  data: HeaderType
}

export const HeaderNav: React.FC<HeaderNavProps> = ({ data }) => {
  const pathname = usePathname()
  const navItems = data?.navItems || []

  const isActive = (url?: string | null) => {
    if (!url) return false
    if (url === '/') return pathname === '/'
    return pathname.startsWith(url)
  }

  const renderNavItem = (item: HeaderNavItem & { id?: string | null }, index: number) => {
    if (item.type === 'dropdown') {
      return <Dropdown key={item.id || index} item={item as HeaderNavItemDropdown} />
    }

    const reference =
      item.reference?.relationTo && item.reference.value
        ? {
            relationTo: item.reference.relationTo,
            value: item.reference.value,
          }
        : null
    const referenceSlug =
      reference &&
      typeof reference.value === 'object' &&
      'slug' in reference.value &&
      typeof reference.value.slug === 'string'
        ? reference.value.slug
        : null
    const url =
      item.linkType === 'reference' && reference && referenceSlug
        ? `${reference.relationTo === 'pages' ? '' : `/${reference.relationTo}`}/${referenceSlug}`
        : item.url || '#'
    const active = isActive(url)

    return (
      <CMSLink
        key={item.id || index}
        type={item.linkType === 'reference' ? 'reference' : 'custom'}
        url={item.url}
        reference={reference}
        newTab={item.newTab}
        label=""
        appearance="inline"
        className={cn(
          'text-sm font-medium transition-colors duration-200 relative py-1 inline-flex items-center gap-1.5',
          active ? 'text-brand-red' : 'text-foreground/80 hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 rounded-sm',
          'after:content-[""] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-brand-red after:rounded-full',
        )}
      >
        {item.icon && typeof item.icon === 'object' && 'url' in item.icon && item.icon.url && (
          <img
            src={item.icon.url}
            alt=""
            width={16}
            height={16}
            className="h-4 w-4 object-contain"
          />
        )}
        {item.label}
      </CMSLink>
    )
  }

  return (
    <nav className="hidden lg:flex lg:items-center lg:gap-6" aria-label="Main navigation">
      {navItems.length > 0 ? navItems.map((item, i) => renderNavItem(item, i)) : null}

      {data?.searchEnabled !== false && (
        <Link
          href="/search"
          className="text-foreground/80 hover:text-foreground transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 rounded-sm"
          aria-label="Search"
        >
          <SearchIcon className="h-5 w-5" aria-hidden="true" />
        </Link>
      )}

      {data?.loginButton?.enabled && (
        <Button variant="ghost" size="sm" asChild className="text-sm font-medium">
          <Link href={data.loginButton.url || '/t/login'}>{data.loginButton.label || 'Login'}</Link>
        </Button>
      )}

      {data?.registerButton?.enabled && (
        <Button variant="ghost" size="sm" asChild className="text-sm font-medium">
          <Link href={data.registerButton.url || '/t/register'}>
            {data.registerButton.label || 'Register'}
          </Link>
        </Button>
      )}

      {data?.contactButton?.enabled && (
        <Button
          size="sm"
          asChild
          className="bg-brand-red text-white hover:bg-brand-red/90 text-sm font-medium"
        >
          <Link
            href={data.contactButton.url || '/contact'}
            className="inline-flex items-center gap-1"
          >
            {data.contactButton.label || 'Contact Us'}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      )}
    </nav>
  )
}
